import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, ShieldCheck, Sparkles, Target, Users, Handshake, TrendingUp } from "lucide-react";

const SERVICES = [
    { icon: Target, title: "Direct Hire Placement", detail: "End-to-end sourcing, screening, and delivery for permanent roles." },
    { icon: TrendingUp, title: "Executive Search", detail: "Confidential search for VP, C-suite, and board seats." },
    { icon: Sparkles, title: "Hard-to-Fill Searches", detail: "Niche, specialized, or hyper-competitive roles our AI alone can't crack." },
    { icon: Users, title: "Candidate Outreach", detail: "Personalized outreach to your top passive prospects." },
    { icon: Handshake, title: "Interview Coordination", detail: "Scheduling, prep, panel management, and offer negotiation." },
    { icon: ShieldCheck, title: "Recruiting Strategy", detail: "Hiring plan, comp benchmarking, employer brand advisory." },
];

export default function Recruiting() {
    return (
        <div className="bg-background" data-testid="recruiting-page">
            {/* Hero */}
            <section className="pt-16 pb-10 relative overflow-hidden">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="max-w-3xl">
                        <p className="overline text-emerald-600">Simonara Recruit™ · optional premium service</p>
                        <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-foreground">
                            Premium recruiting, powered by our AI platform.
                        </h1>
                        <p className="mt-4 text-base text-muted-foreground">
                            Technology first — always. When a role needs a human recruiter, Simonara Recruit™ layers on top of the platform. Never bundled into subscriptions. Contingency-based: no placement, no fee.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Link to="/recruiting/inquiry" className="btn-green text-sm inline-flex items-center gap-1" data-testid="recruit-hero-cta">
                                Start a search <ArrowRight size={13} />
                            </Link>
                            <Link to="/pricing" className="btn-outline text-sm inline-flex items-center gap-1">
                                See software pricing
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Fees */}
            <section className="pb-8">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="card-soft p-6" data-testid="recruit-fee-standard">
                            <div className="overline">Standard placement fee</div>
                            <div className="mt-2 font-heading font-bold text-4xl text-foreground">15%</div>
                            <p className="mt-1 text-sm text-muted-foreground">of the placed candidate's first-year salary. Contingency-based: you pay only after a successful hire.</p>
                        </div>
                        <div className="card-soft p-6 ring-2 ring-emerald-500/60 shadow-lg" data-testid="recruit-fee-founding">
                            <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"><Award size={11} /> Founding rate</div>
                            <div className="mt-2 font-heading font-bold text-4xl text-emerald-600">12%</div>
                            <p className="mt-1 text-sm text-muted-foreground">Founding employers lock in a permanent 12% placement fee — every hire, forever. Includes a Founding Employer badge and featured placement.</p>
                        </div>
                        <div className="card-soft p-6" data-testid="recruit-fee-flat">
                            <div className="overline">Flat-fee option</div>
                            <div className="mt-2 font-heading font-bold text-4xl text-foreground">Available</div>
                            <p className="mt-1 text-sm text-muted-foreground">Predictable pricing available for certain roles and volume commitments. Contact us for a quote.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services grid */}
            <section className="pb-10">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="mb-5">
                        <div className="overline">What's included</div>
                        <h2 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                            Human expertise, layered on Simonara AI.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {SERVICES.map((s) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.35 }}
                                className="card-soft p-5"
                                data-testid={`recruit-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <s.icon size={18} className="text-emerald-500" />
                                <h3 className="mt-2 font-heading font-semibold text-sm text-foreground">{s.title}</h3>
                                <p className="mt-1 text-xs text-muted-foreground">{s.detail}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Guarantee section removed pending legal review (iter 146).
                The "60-day Hiring Success Guarantee" claim was not approved.
                Do NOT re-introduce guarantee language on any recruiting
                surface without written sign-off from legal. */}

            {/* Positioning reminder */}
            <section className="pb-16">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="card-soft p-6">
                        <div className="overline">Technology first. Recruiting expertise second.</div>
                        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
                            Simonara is <b className="text-foreground">The Verified Talent Platform™</b>. Every employer starts with the AI — Talent Passports, Hiring Briefs, Matching. Simonara Recruit™ is an optional add-on for the hard hires. You should never have to pay a recruiter fee to use our software.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
